import React, { useState } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFetch, PageHeader, Loading, StatusBadge, FileUpload, fileSrc } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, CheckCircle2, XCircle, Eye, Upload } from "lucide-react";

export default function Documents() {
  const { data: players, loading, refetch } = useFetch("/players?sort_by=name_asc", []);
  const { data: settings } = useFetch("/settings", []);
  const [edit, setEdit] = useState(null);
  const [docs, setDocs] = useState([]);
  const docTypes = settings?.document_types || [];

  const open = (p) => {
    const existing = p.documents || [];
    const map = {};
    existing.forEach((d) => (map[d.type] = d));
    setDocs(docTypes.map((t) => ({ type: t, status: map[t]?.status || "missing", file: map[t]?.file || null })));
    setEdit(p);
  };
  const save = async () => {
    try { await client.put(`/players/${edit.id}/documents`, { documents: docs }); toast.success("Documents updated"); setEdit(null); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const completeness = (p) => {
    const c = (p.documents || []).filter((d) => d.status === "complete").length;
    return `${c}/${docTypes.length || (p.documents || []).length || 0}`;
  };

  if (loading) return <Loading />;
  return (
    <div>
      <PageHeader title="Documents" subtitle="Track required documents for each player" testid="documents-page" />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border"><tr>
              {["Player", "ID", "Category", "Completeness", ""].map((h) => <th key={h} className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border">
              {(players || []).map((p) => (
                <tr key={p.id} className="hover:bg-muted/40" data-testid={`doc-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.full_name}</td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{p.player_code}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.category} /></td>
                  <td className="px-4 py-3 font-semibold">{completeness(p)}</td>
                  <td className="px-4 py-3 text-end"><Button size="sm" variant="outline" onClick={() => open(p)} data-testid={`doc-edit-${p.id}`}><FileText size={15} className="me-1" /> Checklist</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Documents — {edit?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2 max-h-[60vh] overflow-y-auto">
            {docs.map((d, i) => (
              <div key={d.type} className="flex items-center justify-between gap-3 p-2 rounded-lg border border-border">
                <span className="text-sm font-medium flex items-center gap-2">{d.status === "complete" ? <CheckCircle2 size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-rose-500" />}{d.type}</span>
                <div className="flex items-center gap-2">
                  {d.file?.url && <Button size="icon" variant="ghost" title="Preview" onClick={() => window.open(fileSrc(d.file.url), "_blank")} data-testid={`doc-preview-${i}`}><Eye size={15} /></Button>}
                  <FileUpload accept="image/*,application/pdf" size="icon" label={<Upload size={14} />} testid={`doc-upload-${i}`}
                    onUploaded={(f) => setDocs(docs.map((x, j) => j === i ? { ...x, file: { url: f.url, filename: f.filename }, status: "complete" } : x))} />
                  <Select value={d.status} onValueChange={(v) => setDocs(docs.map((x, j) => j === i ? { ...x, status: v } : x))}>
                    <SelectTrigger className="w-32" data-testid={`doc-status-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="complete">Complete</SelectItem><SelectItem value="missing">Missing</SelectItem><SelectItem value="expired">Expired</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid="doc-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
